import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Edit2, Trash2, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { rbacService, type Role } from '../services/rbacService';
import type { Permission } from '../types';
import { fieldClass, labelClass } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';
import { useAuth } from '../context/AuthContext';

type RoleForm = {
  name: string;
  slug: string;
  description: string;
  permissions: string[];
};

const emptyForm = (): RoleForm => ({
  name: '',
  slug: '',
  description: '',
  permissions: [],
});

const PERMISSION_GROUPS: { label: string; prefix: string }[] = [
  { label: 'داشبورد', prefix: 'dashboard' },
  { label: 'محصولات', prefix: 'products' },
  { label: 'دسته‌بندی', prefix: 'categories' },
  { label: 'بنرها', prefix: 'banners' },
  { label: 'هدر سایت', prefix: 'headers' },
  { label: 'کروسل‌ها', prefix: 'carousels' },
  { label: 'لندینگ‌ها', prefix: 'landings' },
  { label: 'لینک کوتاه', prefix: 'short_links' },
  { label: 'لیدها', prefix: 'leads' },
  { label: 'شغل‌ها', prefix: 'jobs' },
  { label: 'ارسال', prefix: 'shipping' },
  { label: 'کوپن‌ها', prefix: 'coupons' },
  { label: 'سفارش‌ها', prefix: 'orders' },
  { label: 'مشتریان', prefix: 'customers' },
  { label: 'انبار', prefix: 'inventory' },
  { label: 'بلاگ', prefix: 'blog' },
  { label: 'نظرات', prefix: 'reviews' },
  { label: 'حسابرسی', prefix: 'audit' },
  { label: 'آپلود', prefix: 'uploads' },
  { label: 'ادمین‌ها', prefix: 'admins' },
  { label: 'نقش‌ها', prefix: 'roles' },
];

function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 40);
}

export function RolesPage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('roles.write');

  const [roles, setRoles] = useState<Role[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [form, setForm] = useState<RoleForm>(emptyForm());

  const groupedPermissions = useMemo(() => {
    const used = new Set<string>();
    const groups = PERMISSION_GROUPS.map((g) => {
      const items = allPermissions.filter((p) => {
        const match = p.slug.startsWith(g.prefix + '.');
        if (match) used.add(p.slug);
        return match;
      });
      return { ...g, items };
    }).filter((g) => g.items.length > 0);
    const rest = allPermissions.filter((p) => !used.has(p.slug));
    if (rest.length) {
      groups.push({ label: 'سایر', prefix: 'other', items: rest });
    }
    return groups;
  }, [allPermissions]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [roleList, perms] = await Promise.all([
        rbacService.listRoles(),
        rbacService.listPermissions(),
      ]);
      setRoles(roleList);
      setAllPermissions(perms);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت نقش‌ها');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (row: Role) => {
    setEditing(row);
    setForm({
      name: row.name,
      slug: row.slug,
      description: row.description,
      permissions: [...row.permissions],
    });
    setFormOpen(true);
  };

  const togglePerm = (slug: string) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(slug)
        ? prev.permissions.filter((p) => p !== slug)
        : [...prev.permissions, slug],
    }));
  };

  const toggleGroup = (slugs: string[]) => {
    const allSelected = slugs.every((s) => form.permissions.includes(s));
    setForm((prev) => {
      if (allSelected) {
        return { ...prev, permissions: prev.permissions.filter((p) => !slugs.includes(p)) };
      }
      const merged = new Set([...prev.permissions, ...slugs]);
      return { ...prev, permissions: [...merged] };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('نام نقش الزامی است');
      return;
    }
    const slug = editing ? editing.slug : form.slug.trim() || slugify(form.name);
    if (!slug) {
      toast.error('شناسه نقش الزامی است');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await rbacService.updateRole(editing.id, {
          name: form.name.trim(),
          description: form.description.trim(),
          permissions: form.permissions,
        });
        toast.success('نقش به‌روزرسانی شد');
      } else {
        await rbacService.createRole({
          name: form.name.trim(),
          slug,
          description: form.description.trim(),
          permissions: form.permissions,
        });
        toast.success('نقش جدید ایجاد شد');
      }
      closeForm();
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در ذخیره');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: Role) => {
    if (row.isSystem) {
      toast.error('نقش سیستمی قابل حذف نیست');
      return;
    }
    if (row.adminCount > 0) {
      toast.error('این نقش به ادمین‌ها اختصاص داده شده است');
      return;
    }
    if (!window.confirm(`نقش «${row.name}» حذف شود؟`)) return;
    try {
      await rbacService.deleteRole(row.id);
      toast.success('نقش حذف شد');
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در حذف');
    }
  };

  const isSuperAdminRole = editing?.slug === 'super_admin';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-2xl flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-indigo-600" />
            نقش‌ها و دسترسی‌ها
          </h1>
          <p className="text-slate-600 mt-1 text-sm">تعریف نقش و تعیین permission برای هر نقش</p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={openCreate}
            className="w-full sm:w-auto inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            نقش جدید
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="admin-table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-right font-medium text-slate-600">#</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">نام</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">شناسه</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">دسترسی‌ها</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">ادمین‌ها</th>
                {canWrite && <th className="px-4 py-3 text-right font-medium text-slate-600">عملیات</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={canWrite ? 6 : 5} className="px-4 py-8 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : roles.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 6 : 5} className="px-4 py-8 text-center text-slate-500">
                    نقشی یافت نشد
                  </td>
                </tr>
              ) : (
                roles.map((role, idx) => (
                  <tr key={role.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-500">{rowNumber(1, roles.length, idx)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {role.name}
                      {role.isSystem && (
                        <span className="mr-2 text-xs text-slate-400">(سیستمی)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs" dir="ltr">
                      {role.slug}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {role.slug === 'super_admin' ? (
                        <span className="text-indigo-600">همه دسترسی‌ها</span>
                      ) : (
                        `${role.permissions.length} دسترسی`
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{role.adminCount}</td>
                    {canWrite && (
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(role)}
                            className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                            title="ویرایش"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          {!role.isSystem && role.adminCount === 0 && (
                            <button
                              type="button"
                              onClick={() => void handleDelete(role)}
                              className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                              title="حذف"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? 'ویرایش نقش' : 'نقش جدید'}
        size="xl"
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>نام نقش</label>
              <input
                className={fieldClass}
                value={form.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value,
                    slug: editing ? form.slug : slugify(e.target.value),
                  })
                }
              />
            </div>
            {!editing && (
              <div>
                <label className={labelClass}>شناسه (slug)</label>
                <input
                  dir="ltr"
                  className={fieldClass}
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="order_manager"
                />
              </div>
            )}
          </div>
          <div>
            <label className={labelClass}>توضیحات</label>
            <input
              className={fieldClass}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>

          <div>
            <label className={labelClass}>دسترسی‌ها</label>
            {isSuperAdminRole ? (
              <p className="text-sm text-slate-500 mt-1">
                نقش مدیر کل به‌صورت خودکار به همه دسترسی‌ها دسترسی دارد.
              </p>
            ) : (
              <div className="mt-2 space-y-4 border border-slate-200 rounded-lg p-4">
                {groupedPermissions.map((group) => {
                  const slugs = group.items.map((p) => p.slug);
                  const allOn = slugs.every((s) => form.permissions.includes(s));
                  return (
                    <div key={group.prefix}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(slugs)}
                        className="text-sm font-medium text-indigo-700 hover:underline mb-2"
                      >
                        {group.label} {allOn ? '(حذف همه)' : '(انتخاب همه)'}
                      </button>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {group.items.map((p) => (
                          <label
                            key={p.slug}
                            className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              checked={form.permissions.includes(p.slug)}
                              onChange={() => togglePerm(p.slug)}
                              className="mt-0.5 rounded border-slate-300"
                            />
                            <span>
                              <span className="text-slate-800">{p.name}</span>
                              <span className="block text-xs text-slate-400 font-mono" dir="ltr">
                                {p.slug}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              انصراف
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
